export type OAuthProvider = 'email' | 'google' | 'azure' | 'discord' | 'github'

export const OAUTH_PROVIDERS: { id: OAuthProvider; label: string; icon: string }[] = [
  { id: 'email', label: 'Email', icon: 'Mail' },
  { id: 'google', label: 'Google', icon: 'Google' },
  { id: 'azure', label: 'Microsoft', icon: 'Microsoft' },
  { id: 'discord', label: 'Discord', icon: 'Discord' },
  { id: 'github', label: 'GitHub', icon: 'GitHub' }
]

export interface Application {
  id: string
  created_at: string
  updated_at: string
  name: string
  description: string | null
  app_key: string
  app_secret: string
  image_url: string | null
  primary_color: string
  theme_color: string
  redirect_urls: string[]
  allowed_providers: OAuthProvider[]
  owner_id: string
  is_active: boolean
  allows_sso: boolean
}

export interface ApplicationInsert {
  name: string
  description?: string
  image_url?: string
  primary_color?: string
  theme_color?: string
  redirect_urls?: string[]
  allowed_providers?: OAuthProvider[]
}

export interface ApplicationUpdate {
  name?: string
  description?: string
  image_url?: string
  primary_color?: string
  theme_color?: string
  redirect_urls?: string[]
  allowed_providers?: OAuthProvider[]
  is_active?: boolean
  allows_sso?: boolean
}

// =====================================================
// SECURITY FEATURES TYPES
// =====================================================

export type AuthMethod = 'email' | 'oauth_google' | 'oauth_azure' | 'oauth_github' | 'oauth_discord' | 'magic_link' | 'passkey'

export type SecurityNotificationType =
  | 'new_device'
  | 'password_changed'
  | 'mfa_enabled'
  | 'mfa_disabled'
  | 'suspicious_login'
  | 'account_locked'
  | 'account_deletion_scheduled'
  | 'account_deletion_cancelled'

export interface LoginHistory {
  id: string
  created_at: string
  user_id: string
  ip_address: string | null
  user_agent: string | null
  device_type: string | null
  browser: string | null
  os: string | null
  country: string | null
  city: string | null
  success: boolean
  failure_reason: string | null
  auth_method: AuthMethod
  session_id: string | null
}

export interface SecurityNotification {
  id: string
  created_at: string
  user_id: string
  type: SecurityNotificationType
  title: string
  message: string
  metadata: Record<string, unknown>
  read: boolean
  read_at: string | null
  email_sent: boolean
  email_sent_at: string | null
}

export interface Passkey {
  id: string
  created_at: string
  updated_at: string
  user_id: string
  credential_id: string
  public_key: string
  counter: number
  device_type: 'platform' | 'cross-platform' | null
  transports: string[] | null
  name: string | null
  last_used_at: string | null
  backed_up: boolean
}

export interface TrustedDevice {
  id: string
  created_at: string
  updated_at: string
  user_id: string
  device_token: string
  device_name: string | null
  device_type: string | null
  browser: string | null
  os: string | null
  ip_address: string | null
  last_used_at: string
  expires_at: string
  revoked: boolean
  revoked_at: string | null
}

export interface PasswordChange {
  id: string
  created_at: string
  user_id: string
  ip_address: string | null
  user_agent: string | null
  invalidate_sessions: boolean
  sessions_invalidated_at: string | null
}

export interface AccountDeletionRequest {
  id: string
  created_at: string
  user_id: string
  reason: string | null
  scheduled_deletion_at: string
  cancelled: boolean
  cancelled_at: string | null
  executed: boolean
  executed_at: string | null
}

export interface UserDataExport {
  exported_at: string
  user_id: string
  profile: Record<string, unknown>
  login_history: LoginHistory[]
  security_notifications: SecurityNotification[]
  passkeys: Array<{
    id: string
    created_at: string
    name: string | null
    device_type: string | null
    last_used_at: string | null
  }>
  trusted_devices: Array<{
    id: string
    created_at: string
    device_name: string | null
    browser: string | null
    os: string | null
    last_used_at: string
  }>
  connected_apps: Array<{
    app_name: string
    first_login: string
    last_login: string
    login_count: number
  }>
}

// =====================================================
// ENTERPRISE SSO TYPES
// =====================================================

export type SsoProtocol = 'saml' | 'oidc'

export interface Organization {
  id: string
  created_at: string
  updated_at: string
  name: string
  slug: string
  logo_url: string | null
  is_active: boolean
}

export interface OrganizationInsert {
  name: string
  slug?: string
  logo_url?: string
}

export interface OrganizationUpdate {
  name?: string
  slug?: string
  logo_url?: string
  is_active?: boolean
}

export interface OrganizationDomain {
  id: string
  created_at: string
  organization_id: string
  domain: string
  verified: boolean
  verification_token: string | null
  verified_at: string | null
}

export type OrgRole = 'owner' | 'admin' | 'sso_admin' | 'viewer' | 'member'

export interface OrganizationMember {
  id: string
  created_at: string
  updated_at: string
  organization_id: string
  user_id: string
  role: OrgRole
  created_via: 'sso' | 'invite' | 'manual'
  last_sso_login_at: string | null
  // Invitation fields
  invite_email?: string | null
  invite_token?: string | null
  invite_sent_at?: string | null
  invite_accepted_at?: string | null
  invited_by?: string | null
}

export interface SsoConnection {
  id: string
  created_at: string
  updated_at: string
  organization_id: string
  app_id: string
  protocol: SsoProtocol
  is_active: boolean

  // SAML
  saml_idp_entity_id: string | null
  saml_idp_sso_url: string | null
  saml_idp_slo_url: string | null
  saml_idp_certificate: string | null
  saml_sp_entity_id: string | null
  saml_name_id_format: string | null

  // OIDC
  oidc_issuer: string | null
  oidc_client_id: string | null
  oidc_client_secret: string | null
  oidc_authorization_url: string | null
  oidc_token_url: string | null
  oidc_userinfo_url: string | null
  oidc_jwks_url: string | null
  oidc_scopes: string[] | null

  // Options
  jit_provisioning: boolean
  default_role: string
  auto_update_profile: boolean
  attribute_mapping: Record<string, string>
}

export interface SsoConnectionInsert {
  organization_id: string
  app_id: string
  protocol: SsoProtocol

  // SAML
  saml_idp_entity_id?: string
  saml_idp_sso_url?: string
  saml_idp_slo_url?: string
  saml_idp_certificate?: string

  // OIDC
  oidc_issuer?: string
  oidc_client_id?: string
  oidc_client_secret?: string
  oidc_authorization_url?: string
  oidc_token_url?: string
  oidc_userinfo_url?: string
  oidc_jwks_url?: string
  oidc_scopes?: string[]

  // Options
  jit_provisioning?: boolean
  default_role?: string
  auto_update_profile?: boolean
  attribute_mapping?: Record<string, string>
}

export interface SsoConnectionUpdate {
  protocol?: SsoProtocol
  is_active?: boolean

  // SAML
  saml_idp_entity_id?: string
  saml_idp_sso_url?: string
  saml_idp_slo_url?: string
  saml_idp_certificate?: string

  // OIDC
  oidc_issuer?: string
  oidc_client_id?: string
  oidc_client_secret?: string
  oidc_authorization_url?: string
  oidc_token_url?: string
  oidc_userinfo_url?: string
  oidc_jwks_url?: string
  oidc_scopes?: string[]

  // Options
  jit_provisioning?: boolean
  default_role?: string
  auto_update_profile?: boolean
  attribute_mapping?: Record<string, string>
}

export interface SsoLoginAttempt {
  id: string
  created_at: string
  sso_connection_id: string | null
  email: string | null
  status: 'initiated' | 'success' | 'failed'
  error_message: string | null
  ip_address: string | null
  user_agent: string | null
  completed_at: string | null
}

// Response from get_sso_connection_for_domain RPC
export interface SsoConnectionForDomain {
  id: string
  organization_id: string
  organization_name: string
  organization_slug: string
  protocol: SsoProtocol
  saml_idp_sso_url: string | null
  oidc_authorization_url: string | null
  oidc_client_id: string | null
  oidc_scopes: string[] | null
}

export interface SsoStatistics {
  total_organizations: number
  total_sso_connections: number
  total_organization_members: number
  sso_logins_24h: number
  sso_failures_24h: number
  organizations_by_protocol: Record<string, number>
}

// =====================================================
// ORG MANAGER EXTENSION TYPES
// =====================================================

export type OrgAuditAction =
  | 'member_invited'
  | 'member_removed'
  | 'member_role_changed'
  | 'domain_added'
  | 'domain_removed'
  | 'domain_verified'
  | 'sso_created'
  | 'sso_updated'
  | 'sso_deleted'
  | 'sso_toggled'
  | 'settings_updated'
  | 'app_enabled'
  | 'app_disabled'

export interface OrganizationAuditLog {
  id: string
  created_at: string
  organization_id: string
  actor_user_id: string | null
  actor_email: string | null
  actor_ip_address: string | null
  action: OrgAuditAction
  target_type: 'member' | 'domain' | 'sso_connection' | 'settings' | 'app' | null
  target_id: string | null
  target_label: string | null
  old_value: Record<string, unknown> | null
  new_value: Record<string, unknown> | null
  metadata: Record<string, unknown>
}

export interface OrganizationApp {
  id: string
  app_id: string
  app_name: string
  app_key: string
  app_description: string | null
  app_image_url: string | null
  is_enabled: boolean
  is_mandatory: boolean
  enabled_at: string | null
  has_sso_connection: boolean
  sso_connection_id: string | null
  total_logins: number
  logins_30d: number
  unique_users_30d: number
  last_login_at: string | null
  active_subscriptions: number
  total_subscriptions: number
  subscription_plans: { plan: string; count: number }[]
}

export interface OrgAnalytics {
  logins_by_day: Array<{
    date: string
    success: number
    failed: number
  }>
  logins_by_app: Array<{
    app_name: string
    app_image_url: string | null
    count: number
  }>
  top_users: Array<{
    email: string
    login_count: number
    last_login: string
  }>
  active_users_7d: number
  active_users_30d: number
  total_logins_period: number
  success_rate: number
}

export interface OrgPermission {
  id: string
  name: string
  description: string | null
  category: 'members' | 'domains' | 'sso' | 'apps' | 'settings' | 'audit' | 'analytics'
}

export const ORG_ROLES: { id: OrgRole; label: string; description: string }[] = [
  { id: 'owner', label: 'Owner', description: 'Full access to all organization features' },
  { id: 'admin', label: 'Admin', description: 'Manage members, domains, SSO, apps, and settings' },
  { id: 'sso_admin', label: 'SSO Admin', description: 'Manage SSO connections and domains only' },
  { id: 'viewer', label: 'Viewer', description: 'Read-only access to all features' },
  { id: 'member', label: 'Member', description: 'Basic access, can view members only' }
]
